import type { TextFieldProps } from '@mui/material';
import { Box, TextField, Typography, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import { useSelector } from 'react-redux';
import VariableSuggestions from './VariableSuggestions.tsx';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';
import { useScenarioVariablesFormContext } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioVariablesSettings/ScenarioVariablesFormContext.tsx';

type VariableSuggestionInputProps = {
  inputProps: TextFieldProps;
  value: string;
  onChange: (value: string) => void;
};

const useStyles = makeStyles((theme) => ({
  text: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '& .MuiInputBase-input': {
      lineHeight: '1.3em',
    },
  },
  preview: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    minHeight: '64px',
    fontFamily: 'inherit',
    fontSize: '1rem',
    backgroundColor: theme.palette.background.paper,
    marginTop: theme.spacing(1),
    '& span.variable-valid': {
      color: theme.palette.primary.main,
      fontWeight: 500,
      cursor: 'pointer',
    },
    '& span.variable-invalid': {
      color: theme.palette.error.main,
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'underline dotted',
    },
    '& span.variable-valid:hover, & span.variable-invalid:hover': {
      opacity: 0.8,
    },
  },
}));

export const VARIABLE_MATCH_REGEX = /@\w+/g;

const VariableSuggestionInput = React.forwardRef<
  HTMLInputElement,
  VariableSuggestionInputProps
>((props, ref) => {
  const { value, onChange, inputProps } = props;
  const classes = useStyles();

  const [focused, setFocused] = React.useState(false);

  const variablesContext = useScenarioVariablesFormContext();
  const currentScenario = useSelector(ScenarioSelectors.currentChartScenario);
  const variables = variablesContext?.variables ?? currentScenario?.variables ?? [];

  const [hasSuggestions, setHasSuggestions] = React.useState(false);
  const textFieldRef = React.useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);

  const valueMatch = React.useMemo(() => {
    const atIndex = value.slice(0, textFieldRef.current?.selectionStart ?? 0).lastIndexOf('@');
    if (atIndex === -1) return '';

    // Extract the full variable name up to a delimiter or end of string
    const afterAt = value.slice(atIndex + 1);
    const delimiterMatch = afterAt.match(/[\s.,;:!?()\[\]{}"']/);
    const endIndex = delimiterMatch ? delimiterMatch.index! : afterAt.length;
    const newValue = afterAt.slice(0, endIndex);
    return newValue;
  }, [value]);

  const suggestions = React.useMemo(() => {
    if (valueMatch) {
      return variables.filter((variable) =>
        variable.name.toLowerCase().includes(valueMatch.toLowerCase())
      );
    }
    return variables;
  }, [valueMatch, variables]);

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const oldValue = value;
      const oldCount = oldValue.match(/@/g)?.length ?? 0;
      const newCount = event.target.value.match(/@/g)?.length ?? 0;

      onChange(event.target.value);

      if (newCount > oldCount) {
        setHasSuggestions(true);
        setAnchorEl(event.target);
        setHighlightedIndex(0);
      } else if (newCount < oldCount) {
        setHasSuggestions(false);
        setAnchorEl(null);
        setHighlightedIndex(-1);
      }
    },
    [onChange, value]
  );

  // Check if we have an exact match for the current valueMatch
  const hasExactSuggestion = React.useMemo(() => {
    if (!valueMatch) return true;
    return !!variables.find((variable) => variable.name.toLowerCase() === valueMatch.toLowerCase());
  }, [valueMatch, variables]);

  // Determine if we should show the "Add new variable" button
  const showAddButton = React.useMemo(() => {
    return suggestions.length === 0 || !hasExactSuggestion;
  }, [suggestions.length, hasExactSuggestion]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!hasSuggestions) return;

    const maxIndex = showAddButton ? suggestions.length : suggestions.length - 1;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, maxIndex));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
      } else if (event.key === 'Enter') {
      event.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        // Select a suggestion

        const item = suggestions[highlightedIndex].name;
        const atIndex = value.slice(0, textFieldRef.current?.selectionStart ?? 0).lastIndexOf('@');
        
        // Extract the full variable name up to a delimiter or end of string
        const afterAt = value.slice(atIndex + 1);
        const delimiterMatch = afterAt.match(/[\s.,;:!?()\[\]{}"']/);
        const endIndex = atIndex + 1 + (delimiterMatch ? delimiterMatch.index! : afterAt.length);
        
        const newValue =
          value.slice(0, atIndex + 1) +
          item +
          value.slice(endIndex);
        
        onChange(newValue);
        setHasSuggestions(false);
        setAnchorEl(null);
        setHighlightedIndex(-1);
      } else if (showAddButton && highlightedIndex === suggestions.length) {
        // Open the "Add new variable" dialog
        // This will be handled by the VariableSuggestions component
        // We just need to keep the suggestions open
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setHasSuggestions(false);
      setAnchorEl(null);
      setHighlightedIndex(-1);
    }
  };

  const html = React.useMemo(() => {
    let copy = value.replaceAll('<', '<').replaceAll('>', '>');
    const matches = copy.match(VARIABLE_MATCH_REGEX);

    if (Array.isArray(matches) && matches.length > 0) {
      for (const match of matches) {
        const replaceRegex = new RegExp(`(?<!<span.*">)${match}`);
        const variableExists = variables.find((v) => v.name.toLowerCase() === match.slice(1).toLowerCase());
        const spanClass = variableExists ? 'variable-valid' : 'variable-invalid';
        const title = variableExists ? `Variable: ${match}` : 'Variable not found';
        copy = copy.replace(
          replaceRegex,
          `<span class="${spanClass}" title="${title}">${match}</span>`
        );
      }
    }
    return copy;
  }, [value, variables]);

  return (
    <Box display="flex" flexDirection="column">
      <TextField
        variant="standard"
        {...inputProps}
        value={value}
        inputRef={(input) => {
          if (typeof ref === 'function') {
            ref(input);
          } else {
            if (ref) {
              ref.current = input;
            }
          }
          textFieldRef.current = input;
        }}
        disabled={inputProps.disabled}
        fullWidth={inputProps.fullWidth ?? true}
        multiline={inputProps.multiline ?? true}
        maxRows={inputProps.maxRows ?? 3}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={classes.text}
        helperText={focused ? (inputProps.helperText ?? 'Type @ to insert a variable') : ''}
      />

      {/* <Typography
        component="div"
        className={classes.preview}
        dangerouslySetInnerHTML={{ __html: html }}
      /> */}

      <VariableSuggestions
        hasSuggestions={hasSuggestions}
        setHasSuggestions={setHasSuggestions}
        field={textFieldRef}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        value={value}
        setValue={onChange}
        highlightedIndex={highlightedIndex}
        setHighlightedIndex={setHighlightedIndex}
      />
    </Box>
  );
});

export default VariableSuggestionInput;
